/**
 * JLR SharePoint 上传的第二步弹窗（Pike 项目）。
 *
 * 进入时拉 `/upload-plan/` 只读预览，让用户：
 * 1. 填父链接（默认已填好）；
 * 2. 在父链接下建多个路径 —— 不建则全部放根目录；建了第一个则所有文件默认进它；
 *    建第二个及以上时可以勾选把文件挪进别的路径，一个文件只属于一个路径；
 * 3. 把飞书文档链接转成 PDF（可加水印）；没勾选的会被跳过并明确提示。
 *
 * 提交后由调用方带着认证会话去建任务，转换发生在后台任务里。
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Empty,
  Form,
  Input,
  Modal,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import type {
  JlrConversion,
  JlrPlanFeishuLink,
  JlrPlanGroup,
  JlrUploadPlan,
} from '@/types';
import { getJlrUploadPlan } from '@/api/thirdPartyUpload';
import { DEFAULT_JLR_PARENT_URL } from '@/components/packages/JlrAuthModal';
import { formatFileSize } from '@/utils';
import { useT } from '@/i18n';

const { Text } = Typography;

export interface JlrPlanSubmitValues {
  parentUrl: string;
  groups: JlrPlanGroup[];
  conversions: JlrConversion[];
}

interface PlanPath {
  id: string;
  folder: string;
}

interface JlrUploadPlanModalProps {
  open: boolean;
  releaseId: number;
  /** RPA 发布时带上来的目录；为空则用内置默认值 */
  defaultParentUrl?: string;
  /** 登录会话已失效：回到第一步重新认证，本弹窗的填写状态保留 */
  onReauth: () => void;
  /** 抛异常时弹窗保持打开并显示原因 */
  onSubmit: (values: JlrPlanSubmitValues) => Promise<void>;
  onCancel: () => void;
}

/** 可参与上传的条目：计划里的文件 + 已勾选转换的飞书 PDF */
interface UploadableEntry {
  key: string;
  name: string;
  label: string;
  size: number;
  /** 来自飞书链接的转换产物，大小要等转换完才知道 */
  feishu: boolean;
}

const newPath = (): PlanPath => ({
  id: `p${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
  folder: '',
});

const errorBody = (e: unknown): { error?: string; message?: string } =>
  (e as { response?: { data?: { error?: string; message?: string } } })?.response?.data ?? {};

export const JlrUploadPlanModal: React.FC<JlrUploadPlanModalProps> = ({
  open,
  releaseId,
  defaultParentUrl,
  onReauth,
  onSubmit,
  onCancel,
}) => {
  const { t } = useT();
  const [plan, setPlan] = useState<JlrUploadPlan | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [parentUrl, setParentUrl] = useState(DEFAULT_JLR_PARENT_URL);
  const [paths, setPaths] = useState<PlanPath[]>([]);
  /** 文件键 → 路径 id；缺省表示落在第一个（默认）路径 */
  const [assign, setAssign] = useState<Record<string, string>>({});
  const [convert, setConvert] = useState<Record<string, boolean>>({});
  const [watermark, setWatermark] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const loadPlan = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const preview = await getJlrUploadPlan(releaseId);
      setPlan(preview);
      // RPA 发布带了目标目录：按它预建默认路径。仅在用户还没编辑过路径时填，
      // 「重新检测」不会覆盖用户改动。
      const defaultFolder = (preview.default_folder || '').trim();
      if (defaultFolder) {
        setPaths((prev) => (prev.length > 0 ? prev : [{ ...newPath(), folder: defaultFolder }]));
      }
    } catch (e) {
      setLoadError(errorBody(e).message || t('获取上传文件列表失败，请重试'));
    } finally {
      setLoading(false);
    }
  }, [releaseId, t]);

  useEffect(() => {
    if (!open) return;
    setParentUrl(defaultParentUrl || DEFAULT_JLR_PARENT_URL);
    setPaths([]);
    setAssign({});
    setConvert({});
    setWatermark({});
    setError('');
    void loadPlan();
  }, [open, defaultParentUrl, loadPlan]);

  const convertibleLinks = useMemo(
    () => (plan?.feishu_links ?? []).filter((l) => l.convertible),
    [plan]
  );
  const blockedLinks = useMemo(
    () => (plan?.feishu_links ?? []).filter((l) => !l.convertible),
    [plan]
  );

  /** 计划里所有会参与上传的条目，含已勾选转换的飞书 PDF */
  const entries = useMemo<UploadableEntry[]>(() => {
    const files: UploadableEntry[] = (plan?.files ?? []).map((f) => ({
      key: f.key,
      name: f.name,
      label: f.label,
      size: f.size,
      feishu: false,
    }));
    const feishu = convertibleLinks
      .filter((l) => convert[l.key])
      .map((l) => ({
        key: l.key,
        name: l.pdf_name || l.title || l.label,
        label: l.label,
        size: 0,
        feishu: true,
      }));
    return [...files, ...feishu];
  }, [plan, convertibleLinks, convert]);
  const defaultPathId = paths[0]?.id ?? '';

  /** 文件最终落在哪个路径；未显式分配的都回落到默认路径 */
  const pathIdOf = (key: string): string => {
    if (paths.length === 0) return '';
    const assigned = assign[key];
    if (assigned && paths.some((p) => p.id === assigned)) return assigned;
    return defaultPathId;
  };

  const addPath = () => {
    const limit = plan?.limits.max_groups ?? 10;
    if (paths.length >= limit) return;
    setPaths((prev) => [...prev, newPath()]);
  };

  const removePath = (id: string) => {
    const next = paths.filter((p) => p.id !== id);
    const fallback = next[0]?.id ?? '';
    setPaths(next);
    setAssign((prev) => {
      const updated: Record<string, string> = { ...prev };
      for (const [key, value] of Object.entries(updated)) {
        if (value === id) updated[key] = fallback;
      }
      return updated;
    });
  };

  const toggleFile = (key: string, pathId: string, checked: boolean) => {
    setAssign((prev) => {
      const next = { ...prev };
      if (checked) next[key] = pathId;
      else if (pathId === defaultPathId) return prev; // 默认路径不能取消：文件必须有落点
      else next[key] = defaultPathId;
      return next;
    });
  };

  const convertedLinks = convertibleLinks.filter((l) => convert[l.key]);
  /** 已勾选转换、但 BCR-Bot 读不到文档：必须处理掉才能提交 */
  const permissionBlocked = convertedLinks.filter((l) => l.readable === false);
  const skippedLinks = useMemo(
    () => [
      ...convertibleLinks.filter((l) => !convert[l.key]),
      ...blockedLinks,
    ],
    [convertibleLinks, blockedLinks, convert]
  );

  const handleSubmit = async () => {
    setError('');
    if (permissionBlocked.length > 0) {
      setError(t('有飞书文档没有阅读权限，请先在飞书中添加 BCR-Bot 并重新检测'));
      return;
    }
    const trimmedUrl = parentUrl.trim();
    if (!trimmedUrl) {
      setError(t('请填写父目录链接'));
      return;
    }
    const maxFiles = plan?.limits.max_files ?? 50;
    if (entries.length === 0) {
      setError(t('没有可上传的文件'));
      return;
    }
    if (entries.length > maxFiles) {
      setError(t('一次最多上传 {count} 个文件', { count: maxFiles }));
      return;
    }

    // 路径：空串路径会让多级目录退化到根目录，和「不建路径」语义冲突，必须拦住
    const folders = paths.map((p) => p.folder.trim());
    if (folders.some((f) => !f)) {
      setError(t('请填写所有上传路径，或删除空路径'));
      return;
    }
    if (new Set(folders.map((f) => f.toLowerCase())).size !== folders.length) {
      setError(t('上传路径不能重复'));
      return;
    }

    const groups: JlrPlanGroup[] =
      paths.length === 0
        ? []
        : paths.map((p) => ({
          folder: p.folder.trim(),
          file_keys: entries.filter((e) => pathIdOf(e.key) === p.id).map((e) => e.key),
        }));

    const conversions: JlrConversion[] = convertedLinks.map((l) => ({
      file_key: l.key,
      pdf_name: l.pdf_name,
      watermark: (watermark[l.key] || '').trim(),
    }));

    setSubmitting(true);
    try {
      await onSubmit({ parentUrl: trimmedUrl, groups, conversions });
    } catch (e) {
      const body = errorBody(e);
      if (body.error === 'auth_pending' || body.error === 'auth_failed') {
        setError(
          `${body.message || t('登录已失效')} —— ${t('请点击下方「重新认证」后再提交')}`
        );
      } else {
        setError(body.message || t('提交上传失败'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  /** `elsewhere`：该文件被分配到了别的路径，这里只置灰划掉，不代表不上传 */
  const renderEntry = (entry: UploadableEntry, elsewhere = false) => (
    <Space key={entry.key} size={6} wrap>
      <Text
        type={elsewhere ? 'secondary' : undefined}
        style={elsewhere ? { textDecoration: 'line-through' } : undefined}
      >
        {entry.name}
      </Text>
      {entry.label && <Tag>{entry.label}</Tag>}
      {entry.feishu
        ? <Tag color="blue">{t('转 PDF')}</Tag>
        : entry.size > 0 && <Text type="secondary">{formatFileSize(entry.size)}</Text>}
    </Space>
  );

  return (
    <Modal
      title={t('选择上传路径')}
      open={open}
      onCancel={onCancel}
      destroyOnHidden
      maskClosable={false}
      width={760}
      footer={
        <Space>
          <Button onClick={onCancel} disabled={submitting}>{t('取消')}</Button>
          <Button onClick={onReauth} disabled={submitting}>{t('重新认证')}</Button>
          <Button
            type="primary"
            loading={submitting}
            disabled={loading || !!loadError || permissionBlocked.length > 0}
            onClick={() => void handleSubmit()}
          >
            {t('开始上传')}
          </Button>
        </Space>
      }
    >
      {loading ? (
        <div className="text-center py-12"><Spin /></div>
      ) : loadError ? (
        <Alert
          type="error"
          showIcon
          message={loadError}
          action={<Button size="small" onClick={() => void loadPlan()}>{t('重试')}</Button>}
        />
      ) : (
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          <Form layout="vertical" style={{ marginBottom: 0 }}>
            <Form.Item
              label={t('父目录链接')}
              required
              style={{ marginBottom: 0 }}
              extra={t('默认已填好 package_release 目录，一般不需要改。')}
            >
              <Input.TextArea
                rows={2}
                maxLength={500}
                value={parentUrl}
                onChange={(e) => setParentUrl(e.target.value)}
              />
            </Form.Item>
          </Form>

          <Card
            size="small"
            title={t('上传路径')}
            extra={
              <Button
                size="small"
                icon={<PlusOutlined />}
                onClick={addPath}
                disabled={paths.length >= (plan?.limits.max_groups ?? 10)}
              >
                {t('新建路径')}
              </Button>
            }
          >
            {paths.length === 0 ? (
              <Space orientation="vertical" size={10} style={{ width: '100%' }}>
                <Alert
                  type="info"
                  showIcon
                  message={t('所有文件将上传到父链接根目录')}
                  description={t('如需分类存放，点击「新建路径」在父链接下新建子目录。')}
                />
                <Space orientation="vertical" size={4} style={{ width: '100%' }}>
                  {entries.map((entry) => renderEntry(entry))}
                </Space>
              </Space>
            ) : (
              <Space orientation="vertical" size={12} style={{ width: '100%' }}>
                {paths.map((path, index) => {
                  const isDefault = index === 0;
                  const members = entries.filter((e) => pathIdOf(e.key) === path.id);
                  return (
                    <Card
                      key={path.id}
                      size="small"
                      type="inner"
                      title={
                        <Space size={6}>
                          <Text>{isDefault ? t('默认路径') : t('路径 {index}', { index: index + 1 })}</Text>
                          <Text type="secondary">{members.length} {t('个文件')}</Text>
                        </Space>
                      }
                      extra={
                        <Button
                          size="small"
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => removePath(path.id)}
                        >
                          {t('删除')}
                        </Button>
                      }
                    >
                      <Space orientation="vertical" size={10} style={{ width: '100%' }}>
                        <Input
                          placeholder={t('例如 drop12/sw1/pike_20261029')}
                          maxLength={255}
                          value={path.folder}
                          onChange={(e) =>
                            setPaths((prev) =>
                              prev.map((p) => (p.id === path.id ? { ...p, folder: e.target.value } : p))
                            )
                          }
                        />
                        {isDefault && (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {t('所有未指定其它路径的文件都放在这里。')}
                          </Text>
                        )}
                        <Space orientation="vertical" size={4} style={{ width: '100%' }}>
                          {entries.map((entry) => {
                            const checked = pathIdOf(entry.key) === path.id;
                            return (
                              <Checkbox
                                key={entry.key}
                                checked={checked}
                                disabled={isDefault}
                                onChange={(e) => toggleFile(entry.key, path.id, e.target.checked)}
                              >
                                {renderEntry(entry, !checked)}
                              </Checkbox>
                            );
                          })}
                        </Space>
                      </Space>
                    </Card>
                  );
                })}
              </Space>
            )}
            {paths.length > 1 && (
              <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                {t('勾选即把文件移入该路径；一个文件只能属于一个路径，取消勾选会回到默认路径。')}
              </Text>
            )}
          </Card>

          {entries.length === 0 && (
            <Empty description={t('该产物包没有可上传的原始文件')} />
          )}

          {(convertibleLinks.length > 0 || blockedLinks.length > 0) && (
            <Card size="small" title={t('飞书文档')}>
              <Space orientation="vertical" size={12} style={{ width: '100%' }}>
                {convertibleLinks.map((link) => (
                  <div key={link.key}>
                    <Checkbox
                      checked={!!convert[link.key]}
                      onChange={(e) =>
                        setConvert((prev) => ({ ...prev, [link.key]: e.target.checked }))
                      }
                    >
                      <Space size={6} wrap>
                        <Text>{link.title || link.pdf_name}</Text>
                        <Tag>{link.label}</Tag>
                        <Text type="secondary">{t('转换为 PDF')}</Text>
                      </Space>
                    </Checkbox>
                    {convert[link.key] && (
                      <div style={{ marginLeft: 24, marginTop: 6 }}>
                        <Input
                          placeholder={t('水印内容（可留空）')}
                          maxLength={100}
                          value={watermark[link.key] || ''}
                          onChange={(e) =>
                            setWatermark((prev) => ({ ...prev, [link.key]: e.target.value }))
                          }
                        />
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {t('水印会平铺在每一页上，最多 100 个字符。')}
                        </Text>
                      </div>
                    )}
                    {convert[link.key] && link.readable === false && (
                      <div style={{ marginLeft: 24, marginTop: 6 }}>
                        <Text type="danger">
                          {t('BCR-Bot 没有该文档的阅读权限，请在飞书文档右上角「分享」中添加 BCR-Bot 为可阅读的协作者，然后点击「重新检测」。')}
                        </Text>
                        <Button
                          type="link"
                          size="small"
                          onClick={() => void loadPlan()}
                          style={{ padding: 0, marginLeft: 8 }}
                        >
                          {t('重新检测')}
                        </Button>
                        {link.permission_hint && (
                          <Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                            {link.permission_hint}
                          </Text>
                        )}
                      </div>
                    )}
                  </div>
                ))}
                {blockedLinks.map((link: JlrPlanFeishuLink) => (
                  <Space key={link.key} size={6} wrap>
                    <Text type="secondary">{link.title || link.pdf_name}</Text>
                    <Tag>{link.label}</Tag>
                    <Tag color="default">{t('不支持转 PDF')}</Tag>
                    {link.unsupported_reason && (
                      <Text type="secondary">{link.unsupported_reason}</Text>
                    )}
                  </Space>
                ))}
              </Space>
            </Card>
          )}

          {skippedLinks.length > 0 && (
            <Alert
              type="warning"
              showIcon
              message={t('以下 {count} 个飞书文档不会上传', { count: skippedLinks.length })}
              description={
                <ul style={{ paddingLeft: 18, margin: 0 }}>
                  {skippedLinks.map((link) => (
                    <li key={link.key}>
                      <Text>{link.title || link.pdf_name}</Text>
                      {link.label && <Tag style={{ marginLeft: 6 }}>{link.label}</Tag>}
                      {!link.convertible && link.unsupported_reason && (
                        <Text type="secondary">（{link.unsupported_reason}）</Text>
                      )}
                    </li>
                  ))}
                </ul>
              }
            />
          )}

          {error && <Alert type="error" showIcon message={error} />}
        </Space>
      )}
    </Modal>
  );
};

export default JlrUploadPlanModal;
